'use client';

import { useEffect, useState } from 'react';
import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { SEARCH_DEBOUNCE_MS } from '@/lib/constants';
import { logger } from '@/lib/utils/logger';
import type { AddressDetails, AddressSuggestion } from './types';

const AUTOCOMPLETE_ENDPOINT = '/address-search/autocomplete';
const DETAILS_ENDPOINT = '/address-search/details';
const MIN_QUERY_LENGTH = 3;

interface AutocompleteState {
  suggestions: AddressSuggestion[];
  isLoading: boolean;
}

/**
 * Debounced autocomplete against the internal address-search proxy. Mirrors the
 * client-intake portal `AddressField`, but authenticates via the standard
 * internal session (`directApiClient`) rather than a portal Bearer token.
 *
 * Returns no suggestions (and never throws) when the query is too short, the
 * lookup fails, or the proxy is unavailable — the caller's manual-entry
 * fallback covers those cases.
 */
export function useAddressAutocomplete(
  query: string,
  enabled: boolean
): AutocompleteState {
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isActive = enabled && debouncedQuery.trim().length >= MIN_QUERY_LENGTH;

  // Clear results during render when the search goes inactive (query too short
  // or disabled) — avoids a synchronous setState inside the effect body.
  if (!isActive && (suggestions.length > 0 || isLoading)) {
    setSuggestions([]);
    setIsLoading(false);
  }

  useEffect(() => {
    if (!isActive) return;

    const controller = new AbortController();
    // Defer the loading flag to a microtask so it isn't a synchronous setState
    // in the effect body.
    Promise.resolve().then(() => {
      if (!controller.signal.aborted) setIsLoading(true);
    });

    directApiClient
      .get<AddressSuggestion[]>(AUTOCOMPLETE_ENDPOINT, {
        params: { input: debouncedQuery },
        signal: controller.signal
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setSuggestions(Array.isArray(data) ? data : []);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // 408 is the direct client's mapping of an aborted/timed-out request.
        if (error instanceof DirectApiError && error.status === 408) return;
        logger.warn('[AddressSearch] autocomplete request failed', error);
        setSuggestions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [isActive, debouncedQuery]);

  return { suggestions, isLoading };
}

/** Resolve a selected prediction to its fully-structured address details. */
export function fetchAddressDetails(placeId: string): Promise<AddressDetails> {
  return directApiClient.get<AddressDetails>(DETAILS_ENDPOINT, {
    params: { placeId }
  });
}
